/**
 * MeSubpageShell — generalised layout for `/me/*` and `/settings/*` sub-pages.
 *
 * Provides:
 *   - CosmosBackdrop
 *   - Shared <AppHeader> (back-chevron + title)
 *   - Scrollable content area
 *
 * Mounted OUTSIDE TabShell — sub-pages have no bottom NavTabs.
 */

import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isFirstHistoryEntry, parentPath } from '../../lib/history-back';
import { CosmosBackdrop } from '../brand/CosmosBackdrop';
import { AppHeader } from './AppHeader';
import styles from './MeSubpageShell.module.css';

export interface MeSubpageShellProps {
  title: string;
  children: ReactNode;
  /**
   * Optional override for the back action. Defaults to `navigate(-1)`, or to the parent path when the
   * page is the first history entry (opened by a startapp link — nothing to go back to).
   */
  onBack?: () => void;
  /** data-testid on the back button. Defaults to the historical 'me-subpage-back-btn'. */
  backTestId?: string;
  /** Optional action at the right edge of the header bar. Absent → header renders as before. */
  right?: ReactNode;
  /** Keep the title on one line with an ellipsis. */
  singleLineTitle?: boolean;
  /** A tab's top screen: no back button. */
  hideBack?: boolean;
  /** Optional test id for the outer root, useful for screen-level assertions. */
  'data-testid'?: string;
}

export function MeSubpageShell({
  title,
  children,
  onBack,
  backTestId = 'me-subpage-back-btn',
  right,
  singleLineTitle,
  hideBack,
  'data-testid': testId,
}: MeSubpageShellProps): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const handleBack = (): void => {
    if (onBack) {
      onBack();
    } else if (isFirstHistoryEntry()) {
      void navigate(parentPath(pathname), { replace: true });
    } else {
      void navigate(-1);
    }
  };

  return (
    <div className={styles.root} data-testid={testId}>
      <CosmosBackdrop />
      <main className={styles.content} role="main" aria-labelledby="me-subpage-heading">
        <AppHeader
          title={title}
          onBack={handleBack}
          backTestId={backTestId}
          headingId="me-subpage-heading"
          right={right}
          singleLineTitle={singleLineTitle}
          hideBack={hideBack}
        />
        <div className={styles.body}>{children}</div>
      </main>
    </div>
  );
}
